import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { subject: "", category: "General", priority: "Normal", message: "" };

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/tickets").then((r) => setTickets(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tickets", form);
      toast.success("Ticket opened");
      setOpen(false); setForm(EMPTY); load();
    } catch { toast.error("Failed to open ticket"); }
  };

  const badge = (s) => {
    const map = { Open: "bg-blue-50 text-blue-700 border-blue-200", "Waiting on Customer": "bg-amber-50 text-amber-700 border-amber-200", Closed: "bg-slate-100 text-slate-600 border-slate-200" };
    return <Badge className={`rounded-md font-mono text-[10px] uppercase tracking-widest border ${map[s] || ""}`}>{s}</Badge>;
  };

  return (
    <div data-testid="tickets-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow mb-2">Support</div>
          <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Support tickets.</h1>
        </div>
        <Button data-testid="new-ticket-btn" onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-md">
          <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.75}/> New ticket
        </Button>
      </div>

      {tickets.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-md p-12 text-center text-slate-500">
          <LifeBuoy className="w-8 h-8 text-slate-300 mx-auto mb-3" strokeWidth={1.5}/>
          No support tickets yet.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-md bg-white divide-y divide-slate-100">
          {tickets.map((t) => (
            <Link key={t.ticket_id} to={`/dashboard/tickets/${t.ticket_id}`} data-testid={`ticket-${t.ticket_id}`} className="flex items-center justify-between p-5 hover:bg-slate-50">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-slate-500">{t.ticket_id}</span>
                  {badge(t.status)}
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{t.priority}</span>
                </div>
                <div className="mt-1 text-sm text-slate-900 truncate">{t.subject}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t.category} · Updated {new Date(t.updated_at).toLocaleString()}</div>
              </div>
              <div className="text-xs text-slate-400">{t.messages?.length || 0} msg</div>
            </Link>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Open a ticket</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Input required placeholder="Subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})}/>
            <div className="grid grid-cols-2 gap-2">
              <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="h-10 px-3 rounded-md border border-slate-300 text-sm">
                <option>General</option><option>Order</option><option>Documentation</option><option>Billing</option>
              </select>
              <select value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})} className="h-10 px-3 rounded-md border border-slate-300 text-sm">
                <option>Low</option><option>Normal</option><option>High</option><option>Urgent</option>
              </select>
            </div>
            <Textarea rows={6} required placeholder="Describe your issue…" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})}/>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">Open ticket</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
